import { _ } from "i18n";
import { formatDate } from "features/discover/functions";
import type { ColumnConfig } from "../../types";
import { renderBoolean } from "../../cellRenderers";
import { DiscoverGamesAnalysisSlaCell } from "../cells/DiscoverGamesAnalysisSlaCell";
import { DiscoverGamesGameStatusDropdownCell } from "../cells/DiscoverGamesGameStatusDropdownCell";
import { DiscoverGamesPriorityDropdownCell } from "../cells/DiscoverGamesPriorityDropdownCell";
import { formatDaysHoursMinutesFromTotalSeconds } from "common/src/utils/dateTimeFormatUtils";
import { isDiscoverGamesGridCellEmpty } from "../formatters";
import {
	DiscoverGamesColumnField,
	DISCOVER_GAMES_COL_WIDTH_DATE_SHORT,
	DISCOVER_GAMES_COL_WIDTH_ERRORS,
	DISCOVER_GAMES_COL_WIDTH_GAME_ANALYSIS_STATUS,
	DISCOVER_GAMES_COL_WIDTH_GAME_STATUS,
	DISCOVER_GAMES_COL_WIDTH_HISTORIES,
	DISCOVER_GAMES_COL_WIDTH_PHYSICAL_METRICS_REQUESTED,
	DISCOVER_GAMES_COL_WIDTH_PRIORITY,
	DISCOVER_GAMES_COL_WIDTH_PROVIDER_TAG,
	DISCOVER_GAMES_COL_WIDTH_SLA,
	DISCOVER_GAMES_COL_WIDTH_TURNAROUND,
} from "../constants";
import { discoverGameRowId } from "../rowHelpers";
import type { DiscoverGamesColumnsParams, GameRow } from "../types";

export function providerTagColumn(p: DiscoverGamesColumnsParams): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.ProviderTag,
		label: _("discover.gamesTable.column.analysisProvider"),
		sortable: true,
		sortField: DiscoverGamesColumnField.ProviderTag,
		minWidth: DISCOVER_GAMES_COL_WIDTH_PROVIDER_TAG.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_PROVIDER_TAG.max,
		cell: (value, row) => {
			const label = isDiscoverGamesGridCellEmpty(value)
				? _("discover.gamesTable.cellValue.none")
				: String(value);
			const gameId = discoverGameRowId(row);
			const onGameAnalysisOpen = p.onGameAnalysisOpen;
			if (onGameAnalysisOpen === undefined || gameId === null) {
				return label;
			}

			return (
				<button
					type="button"
					onClick={(event) => {
						event.stopPropagation();
						onGameAnalysisOpen(gameId);
					}}
					className="discoverGamesTableV3-analysisProviderAction cell-linked"
				>
					{label}
				</button>
			);
		},
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.ProviderTag, DiscoverGamesColumnField.Id],
	};
}

export function gameAnalysisStatusColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.GameAnalysisStatus,
		label: _("discover.gamesTable.column.gameAnalysisStatus"),
		sortable: true,
		sortField: DiscoverGamesColumnField.GameAnalysisStatus,
		minWidth: DISCOVER_GAMES_COL_WIDTH_GAME_ANALYSIS_STATUS.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_GAME_ANALYSIS_STATUS.max,
		cell: (value) => (value as string) || "-",
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.GameAnalysisStatus],
	};
}

export function priorityColumn(p: DiscoverGamesColumnsParams): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.Priority,
		label: _("discover.gamesTable.column.priority"),
		sortable: true,
		sortField: DiscoverGamesColumnField.Priority,
		minWidth: DISCOVER_GAMES_COL_WIDTH_PRIORITY.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_PRIORITY.max,
		cell: (_, row) => (
			<DiscoverGamesPriorityDropdownCell
				row={row}
				isUpdating={p.isClubMetadataUpdating}
				onChange={p.canEditPriority ? p.onPriorityChange : undefined}
				canEditByPermission={p.canEditPriority}
				readOnlyTitle={p.tooltips?.cannotEditPriority}
			/>
		),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.Priority, DiscoverGamesColumnField.GameAnalysisStatus],
	};
}

export function gameStatusColumn(p: DiscoverGamesColumnsParams): ColumnConfig<GameRow> {
	const gameStatusOptions = p.gameStatusOptions ?? [];
	return {
		key: DiscoverGamesColumnField.GameStatus,
		label: _("discover.gamesTable.column.gameStatus"),
		sortable: true,
		sortField: DiscoverGamesColumnField.GameStatus,
		minWidth: DISCOVER_GAMES_COL_WIDTH_GAME_STATUS.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_GAME_STATUS.max,
		cell: (_, row) => (
			<DiscoverGamesGameStatusDropdownCell
				row={row}
				options={gameStatusOptions}
				isUpdating={p.isClubMetadataUpdating}
				onChange={p.canEditGameStatus ? p.onGameStatusChange : undefined}
				readOnlyTitle={p.tooltips?.cannotEditGameStatus}
			/>
		),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.GameStatus],
	};
}

export function gameHistoriesCountColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.GameHistoriesCount,
		label: _("discover.gamesTable.column.histories"),
		sortable: true,
		sortField: DiscoverGamesColumnField.GameHistoriesCount,
		cell: (value) => String((value as number | undefined) ?? 0),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.GameHistoriesCount],
		minWidth: DISCOVER_GAMES_COL_WIDTH_HISTORIES.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_HISTORIES.max,
	};
}

export function analysisTurnaroundColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.AnalysisTurnaround,
		label: _("discover.gamesTable.column.analysisTurnaround"),
		sortable: true,
		sortField: DiscoverGamesColumnField.AnalysisTurnaround,
		cell: (value) => (value as string | null | undefined) ?? "-",
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.AnalysisTurnaround],
		minWidth: DISCOVER_GAMES_COL_WIDTH_TURNAROUND.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_TURNAROUND.max,
	};
}

export function analysisSlaColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.AnalysisSla,
		label: _("discover.gamesTable.column.analysisSla"),
		sortable: true,
		sortField: DiscoverGamesColumnField.AnalysisSla,
		cell: (_, row) => <DiscoverGamesAnalysisSlaCell row={row} />,
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.AnalysisSla, DiscoverGamesColumnField.AnalysisSlaHours],
		minWidth: DISCOVER_GAMES_COL_WIDTH_SLA.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_SLA.max,
	};
}

export function physicalMetricsRequestedColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.PhysicalMetricsRequested,
		label: _("discover.gamesTable.column.physicalMetricsRequested"),
		sortable: true,
		sortField: DiscoverGamesColumnField.PhysicalMetricsRequested,
		minWidth: DISCOVER_GAMES_COL_WIDTH_PHYSICAL_METRICS_REQUESTED.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_PHYSICAL_METRICS_REQUESTED.max,
		cell: (value) => renderBoolean(value as boolean),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: true,
		requiredFields: [DiscoverGamesColumnField.PhysicalMetricsRequested],
	};
}

function slaDurationColumn(
	key: DiscoverGamesColumnField.SlaContent | DiscoverGamesColumnField.SlaExpansion,
	labelKey: "discover.gamesTable.column.slaContent" | "discover.gamesTable.column.slaExpansion",
): ColumnConfig<GameRow> {
	return {
		key,
		label: _(labelKey),
		sortable: true,
		sortField: key,
		cell: (value: GameRow[keyof GameRow]) => {
			const seconds = typeof value === "number" ? value : null;
			if (seconds === null) {
				return "-";
			}
			return formatDaysHoursMinutesFromTotalSeconds(seconds);
		},
		group: _("discover.gamesTable.group.basic"),
		defaultVisible: true,
		requiredFields: [key],
		minWidth: DISCOVER_GAMES_COL_WIDTH_SLA.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_SLA.max,
	};
}

export function slaContentColumn(): ColumnConfig<GameRow> {
	return slaDurationColumn(
		DiscoverGamesColumnField.SlaContent,
		"discover.gamesTable.column.slaContent",
	);
}

export function slaExpansionColumn(): ColumnConfig<GameRow> {
	return slaDurationColumn(
		DiscoverGamesColumnField.SlaExpansion,
		"discover.gamesTable.column.slaExpansion",
	);
}

export function numberOfContentErrorsColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.NumberOfContentErrors,
		label: _("discover.gamesTable.column.numberOfContentErrors"),
		sortable: true,
		sortField: DiscoverGamesColumnField.NumberOfContentErrors,
		cell: (value) => String((value as number | undefined) ?? 0),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: false,
		requiredFields: [DiscoverGamesColumnField.NumberOfContentErrors],
		minWidth: DISCOVER_GAMES_COL_WIDTH_ERRORS.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_ERRORS.max,
	};
}

export function numberOfTaggingErrorsColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.NumberOfTaggingErrors,
		label: _("discover.gamesTable.column.numberOfTaggingErrors"),
		sortable: true,
		sortField: DiscoverGamesColumnField.NumberOfTaggingErrors,
		cell: (value) => String((value as number | undefined) ?? 0),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: false,
		requiredFields: [DiscoverGamesColumnField.NumberOfTaggingErrors],
		minWidth: DISCOVER_GAMES_COL_WIDTH_ERRORS.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_ERRORS.max,
	};
}

export function analysisRequestedAtColumn(): ColumnConfig<GameRow> {
	return {
		key: DiscoverGamesColumnField.AnalysisRequestedAt,
		label: _("discover.gamesTable.column.analysisRequestedAt"),
		sortable: true,
		sortField: DiscoverGamesColumnField.AnalysisRequestedAt,
		minWidth: DISCOVER_GAMES_COL_WIDTH_DATE_SHORT.min,
		maxWidth: DISCOVER_GAMES_COL_WIDTH_DATE_SHORT.max,
		cell: (value) => formatDate(value as string | null),
		group: _("discover.gamesTable.group.analysis"),
		defaultVisible: false,
		requiredFields: [DiscoverGamesColumnField.AnalysisRequestedAt],
	};
}
